import { ArrowRight, XCircle } from "lucide-react";
import { Chapter, Reveal } from "@/components/landing/Reveal";

const OLD_STEPS = [
  "Research",
  "Writing",
  "Rewriting",
  "Storyboarding",
  "Voiceover",
  "Asset hunting",
  "Editing",
  "Export",
];

const PIT_STEPS = ["Research", "Script", "Storyboard", "Production", "Documentary"];

export default function Problem() {
  return (
    <section id="problem" className="relative py-28 sm:py-36" data-testid="problem-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Chapter index="01" label="The Problem" testid="problem-chapter" />

        <div className="grid grid-cols-1 gap-16 lg:grid-cols-2 lg:gap-12">
          <div>
            <Reveal>
              <h2
                className="font-display text-3xl font-bold leading-[1.08] tracking-tight sm:text-4xl lg:text-5xl"
                data-testid="problem-section-title"
              >
                Great stories are buried in the{" "}
                <span className="text-[#9A9A9A]">production process.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15} className="mt-8 space-y-5 text-base leading-relaxed text-[#9A9A9A] sm:text-lg">
              <p>Researching a topic is only the beginning.</p>
              <p>
                Turning hundreds of sources into a coherent story, writing a script, planning
                visuals, recording narration, finding footage, and assembling the final video can
                take weeks or months.
              </p>
              <p className="text-[#F5F5F5]">pitsora is being built to change that.</p>
            </Reveal>
          </div>

          <div className="space-y-5">
            <Reveal delay={0.1}>
              <div
                className="border border-white/8 bg-[#111111] p-7 sm:p-8"
                data-testid="problem-traditional-card"
              >
                <div className="mb-6 flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#666666]">
                    The old way
                  </span>
                  <XCircle className="h-4 w-4 text-[#555555]" strokeWidth={1.5} />
                </div>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-2.5">
                  {OLD_STEPS.map((step, i) => (
                    <span key={step} className="flex items-center gap-2">
                      <span className="font-mono text-xs tracking-[0.12em] text-[#777777] line-through decoration-[#444444]">
                        {step}
                      </span>
                      {i < OLD_STEPS.length - 1 && (
                        <ArrowRight className="h-3 w-3 text-[#3A3A3A]" strokeWidth={1.5} />
                      )}
                    </span>
                  ))}
                </div>
                <div className="mt-7 border-t border-white/5 pt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#555555]">
                  Timeline — weeks, sometimes months
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <div
                className="relative border border-[#D4AF37]/25 bg-gradient-to-b from-[#161616] to-[#0D0D0D] p-7 sm:p-8"
                data-testid="problem-pit-card"
              >
                <div className="absolute left-0 top-0 h-full w-[2px] bg-[#D4AF37]/70" aria-hidden="true" />
                <div className="mb-6 flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                    The PITSORA way
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#666666]">
                    In development
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2.5">
                  {PIT_STEPS.map((step, i) => (
                    <span key={step} className="flex items-center gap-2.5">
                      <span
                        className={`font-mono text-xs tracking-[0.12em] sm:text-sm ${
                          i === PIT_STEPS.length - 1 ? "text-[#D4AF37]" : "text-[#F5F5F5]"
                        }`}
                      >
                        {step}
                      </span>
                      {i < PIT_STEPS.length - 1 && (
                        <ArrowRight className="h-3.5 w-3.5 text-[#D4AF37]/60" strokeWidth={1.5} />
                      )}
                    </span>
                  ))}
                </div>
                <div className="mt-7 border-t border-white/5 pt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#9A9A9A]">
                  One pipeline — every stage builds on the last
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
